import { SelectField, SelectFieldProps } from '@portals/core/src/components/SelectField/SelectField';
import { SelectFieldItem } from '@portals/core/src/components/SelectFieldItem/SelectFieldItem';
import { useField } from 'formik';
import i18next from 'i18next';
import React, { useCallback, useMemo } from 'react';

import { useSelectValidator } from '../../hooks/SelectValidator/SelectValidator';

// eslint-disable-next-line import/no-named-as-default-member
const fixedT = i18next.getFixedT.bind(i18next)(null, 'forms', 'select');

export interface Option {
  id: string;
  text?: string;
}

export type SelectInputProps = Omit<
  SelectFieldProps,
  'name' | 'value' | 'pattern' | 'color' | 'size' | 'defaultValue'
> & {
  name: string;
  helperText?: SelectFieldProps['helperText'];
  required?: SelectFieldProps['required'];

  selectOptions: Option[];

  locked?: SelectFieldProps['locked'];
  t?: (key: string, options?: Record<string, unknown>) => string;
};

/**
 * SelectInput Form Field
 */
export const SelectInput: React.FC<SelectInputProps> = React.memo(
  ({
    name,
    selectOptions,
    locked = false,
    required = false,
    t = fixedT,
    onBlur: onBlurProps,
    onChange: onChangeProps,
    helperText,
    ...props
  }) => {
    const validate = useSelectValidator({ required, t });

    // eslint-disable-next-line no-restricted-syntax
    const [{ onBlur: onBlurInput, onChange: onChangeInput, value }, { error, touched }, helper] = useField<string>({
      name,
      validate,
    });

    // *** DATALIST HELPER

    const datalistId = `${props.id}-datalist`;
    const datalistComponent = useMemo(
      () => (
        <datalist id={datalistId}>
          {selectOptions.map((option) => (
            <option key={option.id} value={option.text} />
          ))}
        </datalist>
      ),
      [datalistId, selectOptions]
    );

    // *** OPTIONS

    const optionsComponent = useMemo(() => {
      return selectOptions.map((option) => (
        <SelectFieldItem key={option.id} id={option.id} value={option.id}>
          {option.text}
        </SelectFieldItem>
      ));
    }, [selectOptions]);

    // *** TEE BLUR & FOCUS EVENTS

    const onBlurCallback = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        onBlurInput?.(event);
        onBlurProps?.(event);
      },
      [onBlurInput, onBlurProps]
    );

    const onChangeCallback = useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        onChangeInput?.(event);
        onChangeProps?.(event);

        if (!touched) {
          await helper.setValue(event.target.value, false);
          await helper.setTouched(true, true);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [touched, onChangeInput, onChangeProps /* helper.setTouched */]
    );

    // *** RENDER

    return (
      <SelectField
        {...props}
        value={value}
        name={name}
        locked={locked}
        error={touched && Boolean(error)}
        helperText={touched && error ? error : helperText}
        onBlur={onBlurCallback}
        onChange={onChangeCallback}
        datalist={datalistComponent}
        datalistId={datalistId}
      >
        {optionsComponent}
      </SelectField>
    );
  }
);

SelectInput.displayName = 'SelectInput';
